import React from 'react';
import {useState, useEffect, useCallback, useRef} from 'react';
import './IdleTimeoutModal.css';

export const useIdleTimeout = ({
                                   timeout = 30 * 60 * 1000, // 30 minutes in milliseconds
                                   promptTimeout = 2 * 60 * 1000, // 2 minutes warning before logout
                                   onIdle,
                                   onActive,
                                   onPrompt,
                                   throttle = 1000
                               }) => {
    const [isIdle, setIsIdle] = useState(false);
    const [isPrompting, setIsPrompting] = useState(false);
    const [remainingTime, setRemainingTime] = useState(promptTimeout);

    const timeoutRef = useRef(null);
    const promptTimeoutRef = useRef(null);
    const countdownRef = useRef(null);
    const throttleRef = useRef(null);
    const lastActiveRef = useRef(Date.now());

    // Events to track for user activity
    const events = [
        'click',
        'keypress',
        'keydown'
    ];

    const clearAllTimers = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (promptTimeoutRef.current) {
            clearTimeout(promptTimeoutRef.current);
            promptTimeoutRef.current = null;
        }
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
    }, []);

    const resetTimer = useCallback(() => {
        clearAllTimers();

        // Reset all states
        setIsIdle(false);
        setIsPrompting(false);
        setRemainingTime(promptTimeout);
        lastActiveRef.current = Date.now();

        // Set timer for warning prompt
        timeoutRef.current = setTimeout(() => {
            setIsPrompting(true);
            setRemainingTime(promptTimeout);
            onPrompt?.();

            // Start countdown
            let timeLeft = promptTimeout;
            countdownRef.current = setInterval(() => {
                timeLeft -= 1000;
                setRemainingTime(timeLeft);

                if (timeLeft <= 0) {
                    clearAllTimers();
                    setIsIdle(true);
                    setIsPrompting(false);
                    onIdle?.();
                }
            }, 1000);

            // Set final timeout as backup
            promptTimeoutRef.current = setTimeout(() => {
                clearAllTimers();
                setIsIdle(true);
                setIsPrompting(false);
                onIdle?.();
            }, promptTimeout);

        }, timeout - promptTimeout);
    }, [timeout, promptTimeout, onIdle, onPrompt, clearAllTimers]);

    const handleActivity = useCallback((event) => {
        // IMPORTANT: Ignore all activity when modal is showing
        // Only the "Stay Active" button should be able to reset the timer
        if (isPrompting) {
            return;
        }

        // For "Stay Active" button, we want to bypass throttling
        const isStayActiveCall = isIdle;

        if (!isStayActiveCall && throttleRef.current) {
            return;
        }

        // Set throttle for regular activity (not for stay active button)
        if (!isStayActiveCall) {
            throttleRef.current = setTimeout(() => {
                throttleRef.current = null;
            }, throttle);
        }

        // Call onActive callback if user was idle
        if (isIdle) {
            onActive?.();
        }

        // Reset the timer
        resetTimer();
    }, [isPrompting, isIdle, onActive, resetTimer, throttle]);

    const stayActive = useCallback(() => {
        // Clear throttle to ensure this works
        if (throttleRef.current) {
            clearTimeout(throttleRef.current);
            throttleRef.current = null;
        }
        // Directly reset the timer (bypass handleActivity which blocks when prompting)
        resetTimer();
        // Call onActive callback if user was prompting
        if (isPrompting) {
            onActive?.();
        }
    }, [resetTimer, isPrompting, isIdle, onActive]);

    const logout = useCallback(() => {
        clearAllTimers();
        setIsIdle(true);
        setIsPrompting(false);
        onIdle?.();
    }, [onIdle, clearAllTimers]);

    // Initialize timer only once on mount
    useEffect(() => {
        resetTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    // Handle event listeners separately
    useEffect(() => {
        // Add event listeners
        events.forEach(event => {
            document.addEventListener(event, handleActivity, true);
        });

        // Cleanup
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity, true);
            });
            if (throttleRef.current) {
                clearTimeout(throttleRef.current);
            }
        };
    }, [handleActivity]);

    return {
        isIdle,
        isPrompting,
        remainingTime,
        stayActive,
        logout,
        resetTimer
    };
};

export const IdleTimeoutModal = ({
                                     isOpen,
                                     remainingTime,
                                     onStayActive,
                                     onLogout
                                 }) => {
    if (!isOpen) return null;

    const formatTime = (milliseconds) => {
        const seconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;

        if (minutes > 0) {
            return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
        }
        return `${remainingSeconds}`;
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onLogout();
        }
    };

    // Embedded styles to ensure visibility
    const overlayStyles = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999, // Very high z-index to ensure it's on top
        backdropFilter: 'blur(2px)'
    };

    const modalStyles = {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        minWidth: '400px',
        maxWidth: '500px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        animation: 'fadeIn 0.3s ease-out'
    };

    const headerStyles = {
        marginBottom: '16px',
        textAlign: 'center'
    };

    const titleStyles = {
        margin: '0 0 12px 0',
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#dc2626'
    };

    const timerStyles = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px'
    };

    const timerValueStyles = {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#dc2626',
        fontFamily: 'monospace'
    };

    const contentStyles = {
        marginBottom: '24px',
        textAlign: 'center'
    };

    const actionsStyles = {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center'
    };

    const buttonBaseStyles = {
        padding: '12px 24px',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    const stayActiveButtonStyles = {
        ...buttonBaseStyles,
        backgroundColor: '#22c55e',
        color: 'white'
    };

    const logoutButtonStyles = {
        ...buttonBaseStyles,
        backgroundColor: '#dc2626',
        color: 'white'
    };

    return (
        <div
            style={overlayStyles}
            role="dialog"
            aria-modal="true"
            aria-labelledby="idle-timeout-title"
            aria-describedby="idle-timeout-description"
            onKeyDown={handleKeyDown}
        >
            <div style={modalStyles}>
                <div style={headerStyles}>
                    <h2 id="idle-timeout-title" style={titleStyles}>
                        Session Timeout Warning
                    </h2>
                    <div style={timerStyles}>
                        <span>Time remaining:</span>
                        <span style={timerValueStyles}>{formatTime(remainingTime)}</span>
                    </div>
                </div>

                <div style={contentStyles}>
                    <p id="idle-timeout-description">
                        Your session will expire due to inactivity. Would you like to stay logged in?
                    </p>
                </div>

                <div style={actionsStyles}>
                    <button
                        style={stayActiveButtonStyles}
                        onClick={onStayActive}
                        autoFocus
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#16a34a';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#22c55e';
                        }}
                    >
                        Stay Logged In
                    </button>
                    <button
                        style={logoutButtonStyles}
                        onClick={onLogout}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#b91c1c';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#dc2626';
                        }}
                    >
                        Logout
                    </button>
                </div>
            </div>
        </div>
    );
};

export const IdleTimeoutProvider = ({
                                        children,
                                        onLogout,
                                        timeoutDuration = 30 * 60 * 1000, // 30 minutes
                                        promptDuration = 2 * 60 * 1000, // 2 minutes warning
                                        enabled = true
                                    }) => {
    const handleLogout = useCallback(() => {
        // Clear any stored tokens/session data
        localStorage.removeItem('authToken');
        sessionStorage.clear();

        // Call the provided logout function
        onLogout?.();
    }, [onLogout]);

    const handlePrompt = useCallback(() => {
        // User idle warning triggered
    }, []);

    const handleActive = useCallback(() => {
        // User became active again
    }, []);

    const {
        isPrompting,
        remainingTime,
        stayActive,
        logout
    } = useIdleTimeout({
        timeout: timeoutDuration,
        promptTimeout: promptDuration,
        onIdle: handleLogout,
        onPrompt: handlePrompt,
        onActive: handleActive
    });

    if (!enabled) {
        return <>{children}</>;
    }

    return (
        <>
            {children}
            <IdleTimeoutModal
                isOpen={isPrompting}
                remainingTime={remainingTime}
                onStayActive={stayActive}
                onLogout={logout}
            />
        </>
    );
};